import axios from 'axios';

// Create an axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for handling tokens, etc.
api.interceptors.request.use(
  (config) => {
    // You can add auth tokens here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Implement retry logic with exponential backoff
    const { config, response } = error;

    // Only retry on network errors or 5xx errors
    if (!response || (response && response.status >= 500)) {
      const retryConfig = config;

      // Set retry count
      retryConfig.retryCount = retryConfig.retryCount || 0;

      // Max retries
      const maxRetries = 3;

      // Check if we've maxed out the retries
      if (retryConfig.retryCount >= maxRetries) {
        return Promise.reject(error);
      }

      // Increase retry count
      retryConfig.retryCount += 1;

      // Create new promise for retry
      const backoff = new Promise((resolve) => {
        setTimeout(() => {
          console.log(`Retrying request (${retryConfig.retryCount}/${maxRetries})...`);
          resolve();
        }, 1000 * (2 ** retryConfig.retryCount)); // Exponential backoff
      });

      // Return the promise with retry
      return backoff.then(() => api(retryConfig));
    }

    return Promise.reject(error);
  }
);

// API methods
export const eventsApi = {
  // Get events within a bounding box and time range
  getEvents: (bbox, from, to, limit = 300) => {
    return api.get('/radar/api/v1/event/list', {
      params: {
        bbox,
        from: from.toISOString(),
        to: to.toISOString(),
        limit,
        event_type: 'strike_cloud_to_ground',
      },
    });
  },

  // Get a single event by ID
  getEventById: (id) => {
    return api.get('/radar/api/v1/event', {
      params: { id },
    });
  },
};

// Location API
export const locationApi = {
  // Send user location to the server
  sendLocation: (deviceId, latitude, longitude) => {
    return api.post('/radar/api/v1/user/location', {
      deviceId,
      latitude,
      longitude,
    });
  },
};

// Notifications API
export const notificationsApi = {
  // Register push notification token
  registerToken: (token, deviceId) => {
    return api.post('/notification-service/api/v1/push/token', {
      token,
      deviceId,
      platform: 'web',
    });
  },

  // Send telegram notification
  sendTelegramNotification: (origin, message) => {
    return api.post('/notification-service/api/v1/telegram', {
      origin,
      message,
    });
  },
};

// Geofence API
export const geofenceApi = {
  getByCode: (code) => {
    return api.get('/radar/api/v1/geofence/by-code', { params: { code } });
  },

  getEventsInZone: (lat, lon, radiusM) => {
    const earthRadius = 6371000;
    const deltaLat = (radiusM / earthRadius) * (180 / Math.PI);
    const deltaLon = deltaLat / Math.cos(lat * Math.PI / 180);
    const bbox = [lon - deltaLon, lat - deltaLat, lon + deltaLon, lat + deltaLat];
    const to = new Date();
    const from = new Date(to.getTime() - 24 * 60 * 60 * 1000);
    return api.get('/radar/api/v1/event/list', {
      params: {
        bbox,
        from: from.toISOString(),
        to: to.toISOString(),
        limit: 50,
        event_type: 'strike_cloud_to_ground',
      },
    });
  },
};

export default api;
